import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/** What the user picked. "auto" follows the OS preference. */
export type ThemeSetting = "light" | "dark" | "auto";
/** The resolved theme actually applied to the DOM. */
type Theme = "light" | "dark";

const STORAGE_KEY = "tdax-theme";

interface ThemeCtxValue {
  /** Resolved theme (auto → system). */
  theme: Theme;
  /** Raw user choice (light/dark/auto). */
  setting: ThemeSetting;
  setSetting: (s: ThemeSetting) => void;
  /** Flip between an explicit light/dark (used by the topbar button). */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeCtxValue>({
  theme: "light",
  setting: "light",
  setSetting: () => {},
  toggleTheme: () => {},
});

function readStored(): ThemeSetting {
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "dark" || v === "auto" ? v : "light";
}

function systemTheme(): Theme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [setting, setSetting] = useState<ThemeSetting>(() => readStored());
  const [system, setSystem] = useState<Theme>(() => systemTheme());

  // Track the OS preference so "auto" stays in sync.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystem(mq.matches ? "dark" : "light");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const theme: Theme = setting === "auto" ? system : setting;

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    window.localStorage.setItem(STORAGE_KEY, setting);
  }, [theme, setting]);

  const toggleTheme = useCallback(() => {
    setSetting(theme === "light" ? "dark" : "light");
  }, [theme]);

  const value = useMemo(
    () => ({ theme, setting, setSetting, toggleTheme }),
    [theme, setting, toggleTheme],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeCtxValue {
  return useContext(ThemeContext);
}
